import { useEffect, useState } from "react";
import api, { apiError, pctIt } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { conRuolo } from "@/lib/ruoli";
import { toast } from "sonner";
import { X, ListPlus } from "lucide-react";
import ImmobiliFermi from "@/components/ImmobiliFermi";

const eur = (n) => (n != null ? `€ ${Math.round(n).toLocaleString("it-IT")}` : "—");
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

function Kpi({ label, value, ids, onOpen, tone = "", testid, sub }) {
  return (
    <button data-testid={testid} onClick={() => onOpen(label, ids || [])} className={`card-immo p-4 text-left hover:-translate-y-0.5 transition-transform ${tone}`}>
      <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">{label}</div>
      <div className="mono font-extrabold text-2xl" data-testid={`${testid}-valore`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-[var(--muted)] mt-0.5">{sub}</div>}
    </button>
  );
}

function Sezione({ titolo, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-display font-bold text-base md:text-lg mb-3">{titolo}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">{children}</div>
    </div>
  );
}

function Lista({ titolo, ids, mappa, onClose }) {
  return (
    <div className="card-immo p-4 mb-8" data-testid="qp-lista">
      <div className="flex items-center mb-2"><h3 className="font-bold text-sm flex-1">{titolo} · {ids.length} immobili</h3>
        <button data-testid="qp-lista-chiudi" onClick={onClose}><X size={16} /></button></div>
      {ids.length === 0 ? <p className="text-sm text-[var(--muted)]">Nessun immobile.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[620px]">
          <thead className="text-[10px] uppercase text-[var(--muted)]"><tr><th className="p-2 text-left">Immobile</th><th className="p-2 text-left">Agente</th><th className="p-2 text-left">Venditore</th><th className="p-2 text-left">Prezzo</th><th className="p-2 text-left">Scadenza</th></tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {ids.map((id) => mappa[id]).filter(Boolean).map((im) => (
              <tr key={im.id} data-testid={`qp-lista-riga-${im.id}`}>
                <td className="p-2"><a href={`/immobili/${im.id}`} className="font-semibold hover:underline">{im.titolo}</a></td>
                <td className="p-2">{im.agente}</td><td className="p-2">{im.venditore}</td>
                <td className="p-2 mono">{eur(im.prezzo)}</td><td className="p-2">{fmtD(im.scadenza)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function Scadenze({ s, mappa, onTask }) {
  const gruppi = [["scaduti", "Scaduti ancora attivi", "bg-red-50 border-red-300 text-red-700"], ["entro_30", "Entro 30 giorni", "bg-orange-50 border-orange-300 text-orange-700"], ["entro_60", "Entro 60 giorni", "bg-yellow-50 border-yellow-300 text-yellow-800"]];
  return (
    <div className="mb-8" data-testid="qp-scadenze">
      <h2 className="font-display font-bold text-base md:text-lg mb-3">Scadenze incarichi</h2>
      {gruppi.map(([k, lab, cls]) => (
        <div key={k} className={`rounded-2xl border p-3 mb-3 ${cls}`} data-testid={`qp-scad-${k}`}>
          <div className="font-bold text-sm mb-1">{lab}: {s[k].valore}</div>
          {s[k].ids.map((id) => mappa[id]).filter(Boolean).map((im) => (
            <div key={im.id} className="flex items-center gap-3 flex-wrap py-1.5 text-sm text-[#111111] border-t border-black/5" data-testid={`qp-scad-riga-${im.id}`}>
              <a href={`/immobili/${im.id}`} className="font-semibold hover:underline flex-1 min-w-[180px]">{im.titolo}</a>
              <span>{im.agente}</span><span className="text-slate-600">{im.venditore}</span><span className="mono">{fmtD(im.scadenza)}</span>
              <button data-testid={`qp-task-rinnovo-${im.id}`} onClick={() => onTask(im)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold"><ListPlus size={13} /> Crea task rinnovo</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function QualitaPortafoglio() {
  const oggi = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ agente: "", dal: oggi.slice(0, 8) + "01", al: oggi });
  const [d, setD] = useState(null);
  const [agenti, setAgenti] = useState([]);
  const [lista, setLista] = useState(null);
  const load = () => api.get("/qualita-portafoglio", { params: f }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { api.get("/agents?con_soci=true").then((r) => setAgenti(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [f]); // eslint-disable-line
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [f]); // eslint-disable-line
  const open = (titolo, ids) => setLista({ titolo, ids });
  const task = async (im) => {
    try { await api.post(`/qualita-portafoglio/task-rinnovo/${im.id}`); toast.success(`Task di rinnovo creato per ${im.agente}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const q = d?.quantita, s = d?.salute, r = d?.risultati;
  return (
    <div className="fade-up" data-testid="qualita-portafoglio-page">
      <SectionTitle sub="Indicatori calcolati in tempo reale dai dati del CRM. Clicca un numero per vedere gli immobili.">Qualità portafoglio</SectionTitle>
      <div className="flex gap-2 flex-wrap items-end mb-6">
        <label className="text-xs">Agente<select data-testid="qp-filtro-agente" value={f.agente} onChange={(e) => setF({ ...f, agente: e.target.value })} className="block mt-1 px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-base sm:text-sm">
          <option value="">Tutti</option>{agenti.map((a) => <option key={a.id} value={a.id}>{conRuolo(a)}</option>)}</select></label>
        <label className="text-xs">Dal<input data-testid="qp-dal" type="date" value={f.dal} onChange={(e) => setF({ ...f, dal: e.target.value })} className="block mt-1 px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-base sm:text-sm" /></label>
        <label className="text-xs">Al<input data-testid="qp-al" type="date" value={f.al} onChange={(e) => setF({ ...f, al: e.target.value })} className="block mt-1 px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-base sm:text-sm" /></label>
      </div>
      {lista && <Lista {...lista} mappa={d?.immobili || {}} onClose={() => setLista(null)} />}
      {!d ? <div className="skeleton h-60 rounded-2xl" /> : (<>
        <Sezione titolo="Quantità">
          <Kpi testid="qp-attivi-vendita" label="Attivi in vendita" value={q.attivi_vendita.valore} ids={q.attivi_vendita.ids} onOpen={open} />
          <Kpi testid="qp-attivi-affitto" label="Attivi in affitto" value={q.attivi_affitto.valore} ids={q.attivi_affitto.ids} onOpen={open} />
          <Kpi testid="qp-nuovi" label="Nuovi incarichi nel periodo" value={q.nuovi_incarichi.valore} ids={q.nuovi_incarichi.ids} onOpen={open} />
          <Kpi testid="qp-valore" label="Valore portafoglio vendita" value={eur(q.valore_vendita.valore)} ids={q.valore_vendita.ids} onOpen={open} />
          <Kpi testid="qp-provvigioni" label="Provvigioni potenziali" value={eur(q.provvigioni_potenziali.valore)} ids={q.provvigioni_potenziali.ids} onOpen={open}
               sub={q.pct_mancante.valore ? `% mancante su ${q.pct_mancante.valore} immobili` : null} />
        </Sezione>
        <Sezione titolo="Salute">
          <Kpi testid="qp-eta" label="Età media incarichi (giorni)" value={s.eta_media.valore} ids={s.eta_media.ids} onOpen={open} />
          <Kpi testid="qp-6mesi" label="Attivi da oltre 6 mesi" value={s.oltre_6_mesi.valore} ids={s.oltre_6_mesi.ids} onOpen={open} />
          <Kpi testid="qp-senza-visite" label="Senza visite (30 giorni)" value={s.senza_visite_30.valore} ids={s.senza_visite_30.ids} onOpen={open} />
          <Kpi testid="qp-prezzo-sopra" label="Prezzo sopra valutazione/OMI" value={s.prezzo_sopra.valore} ids={s.prezzo_sopra.ids} onOpen={open} sub="+10% valutazione o oltre max OMI" />
          <Kpi testid="qp-ribassati" label="Con ribasso fatto" value={s.ribassati.valore} ids={s.ribassati.ids} onOpen={open} />
        </Sezione>
        <Scadenze s={d.scadenze} mappa={d.immobili} onTask={task} />
        <ImmobiliFermi agente={f.agente} />
        <Sezione titolo="Risultati">
          <Kpi testid="qp-venduti-periodo" label="Venduti nel periodo" value={r.venduti_periodo.valore} ids={r.venduti_periodo.ids} onOpen={open} />
          <Kpi testid="qp-venduti-anno" label="Venduti nell'anno" value={r.venduti_anno.valore} ids={r.venduti_anno.ids} onOpen={open} />
          <Kpi testid="qp-giorni-rogito" label="Giorni medi incarico → rogito" value={r.giorni_incarico_rogito.valore} ids={r.giorni_incarico_rogito.ids} onOpen={open} />
          <Kpi testid="qp-sconto" label="Sconto medio su prezzo" value={r.sconto_medio.valore != null ? pctIt(r.sconto_medio.valore) : "—"} ids={r.sconto_medio.ids} onOpen={open} />
          <Kpi testid="qp-esclusiva" label="Incarichi in esclusiva" value={r.pct_esclusiva.valore != null ? pctIt(r.pct_esclusiva.valore) : "—"} ids={r.pct_esclusiva.ids} onOpen={open} />
        </Sezione>
        <h2 className="font-display font-bold text-base md:text-lg mb-3">Classifica agenti</h2>
        <div className="card-immo p-0 overflow-x-auto" data-testid="qp-classifica">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="bg-[#F7F7F8] text-[10px] uppercase text-[var(--muted)]"><tr><th className="p-3 text-left">Agente</th><th className="p-3">Immobili</th><th className="p-3">Valore</th><th className="p-3">Visite</th><th className="p-3">Venduti</th></tr></thead>
            <tbody className="divide-y divide-[var(--border)]">
              {d.classifica.map((c) => (
                <tr key={c.agente_id} data-testid={`qp-classifica-${c.agente_id}`}>
                  <td className="p-3 font-semibold">{c.agente}</td>
                  <td className="p-3 text-center"><button className="underline mono" onClick={() => open(`Immobili di ${c.agente}`, c.ids)}>{c.n}</button></td>
                  <td className="p-3 text-center mono">{eur(c.valore)}</td><td className="p-3 text-center mono">{c.visite}</td><td className="p-3 text-center mono">{c.venduti}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
}
